/**
 * component-related-products.js -- Related Products Web Component for Mirai Online
 *
 * RelatedProducts custom element ("You may also like"):
 *  - Renders an empty placeholder on first paint (height reserved via CSS -> 0 CLS)
 *  - IntersectionObserver fires a single fetch when the section scrolls into view
 *  - Fetches the section's own HTML via the Shopify Recommendations + Section Rendering API
 *    (data-url already carries product_id, intent=related, limit, and section_id)
 *  - Replaces its own innerHTML with the server-rendered recommendations grid
 *  - Hides itself entirely when there are no recommendations (per 03-RESEARCH A4)
 *  - Disconnects the observer after the first successful load (fetch once -> T-03-14)
 *  - Re-initializes on shopify:section:load for theme editor compatibility
 *
 * Security notes (per threat model T-03-12, T-03-14):
 *  - Fetched HTML is Shopify's own Liquid-rendered section output (recommendations) --
 *    same trust model as the Phase 2 collection filters Section Rendering. Safe to inject.
 *  - The fetch URL is the theme's own section_id endpoint, never built from user input.
 *  - Content is injected from a DOMParser-extracted node, not raw string concatenation.
 *  - Observer disconnects after one successful fetch; failures leave the section hidden
 *    with no retry loop (no request flooding).
 */

/* ============================================================
   RELATED PRODUCTS WEB COMPONENT
   ============================================================ */

class RelatedProducts extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._loaded = false;
    this._boundReinit = this.reinit.bind(this);
  }

  connectedCallback() {
    this.setupObserver();

    // Theme editor: re-fetch when the section is reloaded.
    this.addEventListener('shopify:section:load', this._boundReinit);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this.removeEventListener('shopify:section:load', this._boundReinit);
  }

  /**
   * Re-initialize after a theme editor section reload.
   */
  reinit() {
    this._loaded = false;
    this.hidden = false;
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this.setupObserver();
  }

  /**
   * Observe the element; fetch recommendations the first time it nears the viewport.
   * rootMargin pre-loads slightly before the section is fully visible.
   */
  setupObserver() {
    if (this._loaded || !this.dataset.url) return;

    // WR-05: if a REAL recommendations grid was already rendered inline by the
    // Liquid (SSR), there is nothing to fetch — observing/fetching would cause
    // a redundant request and a visible re-render flash.
    // A server-rendered *fallback* grid (data-fallback) is different: it should
    // still try to upgrade to live recommendations, so we do NOT skip the fetch.
    if (this.querySelector('.related-products__grid') && !this.hasAttribute('data-fallback')) {
      this._loaded = true;
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: no IntersectionObserver -> fetch immediately.
      this.loadRecommendations();
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadRecommendations();
            break;
          }
        }
      },
      { rootMargin: '200px 0px' }
    );

    this._observer.observe(this);
  }

  /**
   * Fetch the recommendations section HTML and inject the grid.
   * On zero results or any failure, hide the section (no broken UI, no retry).
   */
  async loadRecommendations() {
    if (this._loaded) return;
    this._loaded = true;

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) {
        throw new Error(`Recommendations fetch failed: ${response.status}`);
      }
      const html = await response.text();
      this.render(html);
    } catch (error) {
      console.warn('[RelatedProducts] loadRecommendations failed:', error);
      // Keep the server-rendered fallback grid if present; only hide if there is
      // genuinely nothing to show.
      if (!this.querySelector('.related-products__grid')) {
        this.hidden = true;
      }
    }
  }

  /**
   * Parse the server-rendered section HTML and replace this element's content.
   * HTML comes from Shopify's server (Liquid-rendered recommendations) -- safe to inject.
   *
   * @param {string} html - Server-rendered section HTML string
   */
  render(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // The Section Rendering response wraps the content in the same <related-products>
    // element; fall back to the .reco container's parent if the wrapper is absent.
    // NB: not the grid's parent — that is the <product-carousel>, so injecting it
    // would drop the carousel wrapper and leave the chevron arrows unbound.
    const source =
      doc.querySelector('related-products') ||
      doc.querySelector('.reco')?.parentElement;

    const hasProducts = doc.querySelector('.reco-card');

    if (!source || !hasProducts) {
      // No live recommendations for this product. Keep the server-rendered
      // fallback grid (same collection / catalog) if we have one — only hide
      // when there is genuinely nothing to show.
      if (!this.querySelector('.related-products__grid')) {
        this.hidden = true;
      }
      this.removeAttribute('data-fallback');
      return;
    }

    // Live recommendations exist -> upgrade the fallback to the real grid.
    this.innerHTML = source.innerHTML;
    this.removeAttribute('data-fallback');
  }
}

// Registration guard: only define once even if the section renders multiple times.
if (!customElements.get('related-products')) {
  customElements.define('related-products', RelatedProducts);
}
